export interface HeatpumpFundingParams {
  /** Gross cost of the heat pump in € */
  cost: number
  /** Number of residential units, >= 1 */
  units: number
  /** Grundförderung in % (applies to all units) */
  baseRate: number
  /** Geschwindigkeitsbonus in % for the selected period */
  speedBonus: number
  /** Einkommensbonus in % for the selected income tier */
  incomeBonus: number
  /** Deckel for the first unit's Fördersatz in % */
  capRate: number
  /** Max eligible costs for the first unit in the selected period, in € */
  firstUnitCap: number
  /** Max eligible costs per additional unit, in € */
  additionalUnitCaps: { unit2to6: number; unitFrom7: number }
}

export interface HeatpumpFundingResult {
  /** Sum of all rate components before the Deckel */
  uncappedRate: number
  /** Fördersatz applied to the first unit, capped */
  rate: number
  /** Upper bound of eligible costs across all units */
  maxEligibleCosts: number
  /** Eligible share of the entered costs */
  eligibleCosts: number
  fundingFirstUnit: number
  fundingFurtherUnits: number
  funding: number
  costsAfterFunding: number
}

/**
 * Computes the heat-pump subsidy per the future funding scheme
 * (BMF Regierungsentwurf, Stand 07/2026).
 *
 * Costs are apportioned equally across the units, each unit's share capped
 * by its tier (first unit / units 2–6 / units 7+). Geschwindigkeits- and
 * Einkommensbonus only apply to the self-occupied first unit; further units
 * get the Grundförderung.
 */
export const calculateHeatpumpFunding = ({
  cost,
  units,
  baseRate,
  speedBonus,
  incomeBonus,
  capRate,
  firstUnitCap,
  additionalUnitCaps,
}: HeatpumpFundingParams): HeatpumpFundingResult => {
  const uncappedRate = baseRate + speedBonus + incomeBonus
  const rate = Math.min(uncappedRate, capRate)

  const unitsTier2 = Math.min(Math.max(units - 1, 0), 5)
  const unitsTier3 = Math.max(units - 6, 0)
  const maxEligibleCosts =
    firstUnitCap +
    unitsTier2 * additionalUnitCaps.unit2to6 +
    unitsTier3 * additionalUnitCaps.unitFrom7

  const costPerUnit = cost / units
  const eligibleFirstUnit = Math.min(costPerUnit, firstUnitCap)
  const eligibleFurtherUnits =
    Math.min(costPerUnit, additionalUnitCaps.unit2to6) * unitsTier2 +
    Math.min(costPerUnit, additionalUnitCaps.unitFrom7) * unitsTier3
  const eligibleCosts = Math.round(eligibleFirstUnit + eligibleFurtherUnits)

  const fundingFirstUnit = Math.round((eligibleFirstUnit * rate) / 100)
  const fundingFurtherUnits = Math.round((eligibleFurtherUnits * baseRate) / 100)
  const funding = fundingFirstUnit + fundingFurtherUnits
  const costsAfterFunding = Math.max(cost - funding, 0)

  return {
    uncappedRate,
    rate,
    maxEligibleCosts,
    eligibleCosts,
    fundingFirstUnit,
    fundingFurtherUnits,
    funding,
    costsAfterFunding,
  }
}
